import ModalType from '@/Enums/Pixels/ModalType.enum';
import type { IPixelsFilterModel } from '@/Interfaces/Filters/FilterModel.interface';
import type { IParsedEvent } from '@/Interfaces/Pixels/Event.interface';
import { Grid } from '@/components/Grid';
import Pagination from '@/components/Grid/Pagination';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAdmin } from '@/store/selectors/me.selectors';
import { addNewPixel, resetSelectedPixel, setSelectedPixel, updatePixel } from '@/store/slices/pixelsSlice';
import { getPixelTag, updateActiveStatus } from '@/store/slices/pixelsThunks';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { type GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import PixelModal from '../PixelModal';
import ToolBar from './ToolBar';
import { getColumns } from './columns';

interface IProps {
  data: IParsedEvent[];
  loading: boolean;
  filters: IPixelsFilterModel;
  setFilters: (filters: Partial<IPixelsFilterModel>) => void;
  loadingMetrics: boolean;
}

const DataGrid = ({ data = [], loading, filters, setFilters, loadingMetrics }: IProps) => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsAdmin);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterLogicOperator: GridLogicOperator.Or,
    logicOperator: GridLogicOperator.Or,
    quickFilterValues: [],
  });

  const onFilterModelChange = (newFilterModel: GridFilterModel) => setFilterModel(newFilterModel);

  //#region dispatch
  const getCurrentPixelTag = useCallback(() => {
    dispatch(getPixelTag());
  }, [dispatch]);

  const setCurrentPixel = useCallback(
    (pixel: IParsedEvent) => {
      dispatch(setSelectedPixel(pixel));
    },
    [dispatch],
  );

  const resetCurrentPixel = useCallback(() => {
    dispatch(resetSelectedPixel());
  }, [dispatch]);

  const appendNewPixel = useCallback(
    (newPixel: IParsedEvent) => {
      dispatch(addNewPixel(newPixel));
    },
    [dispatch],
  );

  const _updatePixel = useCallback(
    (pixel: Partial<IParsedEvent>) => {
      dispatch(updatePixel(pixel));
    },
    [dispatch],
  );
  //#endregion

  //#region modals
  const [modalType, setModalType] = useState(ModalType.ViewPixel);
  const [modalOpen, setModalOpen] = useState(false);

  const onNewPixelClose = useCallback(
    (newPixel?: Partial<IParsedEvent>) => {
      if (!newPixel) {
        setModalOpen(false);
        return;
      }
      const hydratedNewPixel = { ...newPixel, active: true } as IParsedEvent;
      setModalType(ModalType.ViewPixel);
      appendNewPixel(hydratedNewPixel);
      setCurrentPixel(hydratedNewPixel);
      getCurrentPixelTag();
      return;
    },
    [appendNewPixel, getCurrentPixelTag, setCurrentPixel],
  );
  const onCreateNewClick = () => {
    setModalOpen(true);
    setModalType(ModalType.NewPixel);
  };

  const onViewPixelClose = useCallback(
    (pixel?: Partial<IParsedEvent>) => {
      setModalOpen(false);
      resetCurrentPixel();
      if (pixel) {
        _updatePixel(pixel);
      }
    },
    [_updatePixel, resetCurrentPixel],
  );
  const onViewPixelClick = useCallback(
    (row: IParsedEvent) => {
      setCurrentPixel(row);
      getCurrentPixelTag();
      setModalType(ModalType.ViewPixel);
      setModalOpen(true);
    },
    [getCurrentPixelTag, setCurrentPixel],
  );

  const archivePixel = useCallback(
    (pixel: { id: number; active: boolean }) => {
      dispatch(updateActiveStatus(pixel));
    },
    [dispatch],
  );

  const handleModalClose = useCallback(
    (pixel?: Partial<IParsedEvent>) => {
      switch (modalType) {
        case ModalType.ViewPixel:
          onViewPixelClose(pixel);
          break;
        case ModalType.NewPixel:
          onNewPixelClose(pixel);
          break;
      }
    },
    [modalType, onNewPixelClose, onViewPixelClose],
  );
  //#endregion

  //#region columns
  const onStatusChange = useCallback(
    (newStatus: string[]) => {
      setFilters({ status: newStatus });
      setFilterModel({
        ...filterModel,
        items: [
          {
            id: 'statusFilter',
            field: 'status',
            value: newStatus,
            operator: 'statusInFilter',
          },
        ],
      });
    },
    [filterModel, setFilters],
  );

  const COLUMNS = useMemo(
    () => getColumns(onViewPixelClick, archivePixel, loadingMetrics, isAdmin),
    [loadingMetrics, archivePixel, onViewPixelClick, isAdmin],
  );
  //#endregion

  return (
    <>
      <Grid
        loading={loading}
        autosizeOnMount
        autosizeOptions={EXPAND_OPTIONS}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        showCellVerticalBorder
        showColumnVerticalBorder
        rows={data}
        columns={COLUMNS}
        columnHeaderHeight={30}
        initialState={{
          pagination: { paginationModel: { pageSize: 15 } },
          sorting: {
            sortModel: [
              {
                field: 'eventId',
                sort: 'desc',
              },
            ],
          },
        }}
        pagination
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        slots={{
          pagination: Pagination,
          toolbar: ToolBar,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
        }}
        slotProps={{
          pagination: {
            loading,
          },
          footer: {
            className: 'relative',
          },
          toolbar: {
            setQuickFilter: quickFilterValues => setFilterModel(prev => ({ ...prev, quickFilterValues })),
            onCreateNewClick,
            setFilters,
            onStatusChange,
            status: filters.status,
            active: filters.active,
            loading,
          },
        }}
      />
      <PixelModal handleClose={handleModalClose} open={modalOpen} modalType={modalType} />
    </>
  );
};

export default DataGrid;
